import {
  MEMBERSHIP_RETURN_KEY,
  MEMBERSHIP_RETURN_PARAM,
  PURCHASE_ELEMENTS,
  isMembershipReturn,
  membershipReturnSession,
  membershipSuccessUrl,
  withoutMembershipReturn,
  type MembershipReturnContext,
  type OpenPurchaseOptions,
  type PurchaseKind,
} from "@baluu/ui/lib/checkout/purchases";
import type { BasketMode } from "@baluu/ui/lib/checkout/basket-surface";
import { apiOrigin } from "./basket-validate";
import { addLineToBasket, checkoutLine, type BasketLine, type LaneOutcome } from "./basket-lanes";
import { ensureBasketElement, requestBasketDrawer } from "./ensure-basket-element";

// What the purchase overlays (gift card, pass, membership, product) share:
// how one opens and closes, how a pass or a product goes on to payment, and
// how a new member's return from Stripe's Checkout is shown. The overlays'
// names are @baluu/ui's, which the pages that open them read too. Pure DOM
// and store calls, so it is tested without rendering.

export { MEMBERSHIP_RETURN_PARAM, PURCHASE_ELEMENTS, type OpenPurchaseOptions, type PurchaseKind };

const DEFAULT_API_URL = "https://api.baluu.io";

/** The API root the overlays call (`api-url` may name it with or without
 *  `/widget`, as the other components accept). */
export function purchaseApiBase(apiUrl: string | undefined): string {
  return apiOrigin(apiUrl || DEFAULT_API_URL);
}

/** Close an overlay: tell the page, and take every copy of it off. */
export function closePurchaseOverlay(kind: PurchaseKind): void {
  const { tag, closeEvent } = PURCHASE_ELEMENTS[kind];
  document.dispatchEvent(new CustomEvent(closeEvent, { bubbles: true, composed: true }));
  document.querySelectorAll(tag).forEach((el) => el.parentNode?.removeChild(el));
}

/**
 * Buy now (a pass, a product, a gift card): the basket checkout opens for the
 * buyer's details, the sign-in a school may ask for, terms, discount codes
 * and payment (createBasketCheckoutSession) — for everything in the basket
 * when the surface offers it (`mode` on or page), else for this line alone
 * (the single-item lane the class checkout takes too; basket-lanes.ts). The
 * overlay closes (`close`) once the checkout has it; a refusal leaves it
 * open, with the reason.
 */
export function proceedToBasketCheckout({
  line,
  businessId,
  apiUrl,
  primaryColor,
  mode,
  close,
}: {
  line: BasketLine;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  mode: BasketMode;
  /** How the overlay that sold it goes. */
  close: () => void;
}): LaneOutcome {
  const outcome = checkoutLine({ line, businessId, apiUrl, primaryColor, keepBasket: mode !== "off" });
  if (outcome.ok) close();
  return outcome;
}

/**
 * "Add to basket" from a purchase overlay (the surface offers the basket):
 * the line joins the basket, the overlay closes and the school's basket
 * opens, so the visitor sees it went in and carries on browsing. Refused
 * (with the reason) when it can't share the basket; the overlay then stays
 * open.
 */
export function addPurchaseToBasket({
  line,
  businessId,
  apiUrl,
  primaryColor,
  mode,
  close,
}: {
  line: BasketLine;
  businessId: string;
  apiUrl?: string;
  primaryColor?: string;
  mode: Exclude<BasketMode, "off">;
  close: () => void;
}): LaneOutcome {
  const added = addLineToBasket(line, businessId);
  if (!added.ok) return added;
  close();
  ensureBasketElement({ businessId, apiUrl, primaryColor, mode });
  requestBasketDrawer(businessId);
  return added;
}

/** Open a purchase overlay on the page (one of a kind at a time); it goes
 *  when it says it closed. Returns the element, or null when one is already
 *  open. `window.BaluuWidgets.openPurchase` — how Website v2 and a school's
 *  own site open them. */
export function openPurchaseOverlay(kind: PurchaseKind, options: OpenPurchaseOptions): HTMLElement | null {
  const { tag, closeEvent } = PURCHASE_ELEMENTS[kind];
  if (document.querySelector(tag)) return null;
  const el = document.createElement(tag);
  el.setAttribute("business-id", options.businessId);
  el.setAttribute("business-slug", options.businessSlug);
  if (options.apiUrl) el.setAttribute("api-url", options.apiUrl);
  if (options.primaryColor) el.setAttribute("primary-color", options.primaryColor);
  if (kind === "giftcard" && options.widgetBaseUrl) el.setAttribute("widget-base-url", options.widgetBaseUrl);
  if (kind === "pass" && options.itemId) el.setAttribute("pass-id", options.itemId);
  if (kind === "membership" && options.itemId) el.setAttribute("plan-id", options.itemId);
  if (kind === "membership" && options.result) el.setAttribute("result", options.result);
  if (kind === "product" && options.productSlug) el.setAttribute("product-slug", options.productSlug);
  // A membership is a subscription: never in the basket.
  if (kind !== "membership" && options.basket && options.basket !== "off") el.setAttribute("basket", options.basket);
  document.body.appendChild(el);
  const close = () => {
    el.remove();
    document.removeEventListener(closeEvent, close);
  };
  document.addEventListener(closeEvent, close);
  return el;
}

/** Where Stripe's Checkout sends the buyer: back to this page (its hash
 *  kept), with the return marker and the session's id on success, and
 *  without them on cancel. */
export function membershipReturnUrls(href: string): { successUrl: string; cancelUrl: string } {
  const url = new URL(withoutMembershipReturn(href));
  url.hash = "";
  return { successUrl: membershipSuccessUrl(href), cancelUrl: url.toString() };
}

// Which school's membership the buyer went to Stripe for, kept in this tab
// for the trip there and back (the return address names only the marker and
// the session; @baluu/ui's MEMBERSHIP_RETURN_KEY, which Website v2 writes
// too).
export type { MembershipReturnContext };

/** Called as the buyer leaves for Stripe's Checkout. */
export function rememberMembershipReturn(context: MembershipReturnContext): void {
  try {
    window.sessionStorage.setItem(MEMBERSHIP_RETURN_KEY, JSON.stringify(context));
  } catch {
    // No storage (a private window): the buyer comes back without the
    // in-page confirmation; Stripe's own receipt still reaches them.
  }
}

// The sessions this browser has already shown a confirmation for, so a
// reload, the back button or a bookmark never shows (and counts) it twice.
const CONFIRMED_KEY = "baluu-membership-confirmed";
const CONFIRMED_KEPT = 20;

function alreadyConfirmed(sessionId: string): boolean {
  try {
    const shown: unknown = JSON.parse(window.localStorage.getItem(CONFIRMED_KEY) || "[]");
    return Array.isArray(shown) && shown.includes(sessionId);
  } catch {
    return false;
  }
}

function markConfirmed(sessionId: string): void {
  try {
    const shown: unknown = JSON.parse(window.localStorage.getItem(CONFIRMED_KEY) || "[]");
    const list = Array.isArray(shown) ? shown.filter((id) => typeof id === "string" && id !== sessionId) : [];
    window.localStorage.setItem(CONFIRMED_KEY, JSON.stringify([...list, sessionId].slice(-CONFIRMED_KEPT)));
  } catch {
    // No storage: nothing to remember it by.
  }
}

/** Whether the API says this Checkout Session completed for the school. */
async function sessionConfirmed(
  context: MembershipReturnContext,
  sessionId: string,
  fetchImpl: typeof fetch,
): Promise<boolean> {
  try {
    const response = await fetchImpl(
      `${purchaseApiBase(context.apiUrl)}/widget/app/membership/checkout-session/${encodeURIComponent(sessionId)}?schoolId=${encodeURIComponent(context.businessId)}`,
    );
    if (!response.ok) return false;
    const body = await response.json().catch(() => null);
    return body?.data?.confirmed === true;
  } catch {
    return false;
  }
}

/**
 * Back from Stripe's Checkout with a new membership: the bundle runs this as
 * it loads, on any page — Website v2 or a school's own site — so the buyer
 * sees the confirmation where they joined. The marker and the session come
 * off the address (a reload or a shared link doesn't say it again), and the
 * membership overlay opens in its confirmation, which counts the conversion,
 * only once the API confirms that session completed for the school: the
 * marker alone is something anyone can type or share. Once per session in
 * this browser. Resolves to the overlay, or null.
 */
export async function resumeMembershipReturn(fetchImpl: typeof fetch = fetch): Promise<HTMLElement | null> {
  if (typeof window === "undefined") return null;
  const href = window.location.href;
  if (!isMembershipReturn(href)) return null;
  const sessionId = membershipReturnSession(href);
  try {
    window.history.replaceState(window.history.state, "", withoutMembershipReturn(href));
  } catch {
    // An address the page can't rewrite: leave it.
  }
  let context: MembershipReturnContext | null = null;
  try {
    context = JSON.parse(window.sessionStorage.getItem(MEMBERSHIP_RETURN_KEY) || "null");
    window.sessionStorage.removeItem(MEMBERSHIP_RETURN_KEY);
  } catch {
    context = null;
  }
  if (!sessionId || !context?.businessId || !context.businessSlug) return null;
  if (alreadyConfirmed(sessionId)) return null;
  if (!(await sessionConfirmed(context, sessionId, fetchImpl))) return null;
  markConfirmed(sessionId);
  return openPurchaseOverlay("membership", {
    businessId: context.businessId,
    businessSlug: context.businessSlug,
    apiUrl: context.apiUrl,
    primaryColor: context.primaryColor,
    itemId: context.planId,
    result: "success",
  });
}

/** One pass or plan when the page asked for it and it's on sale, else all. */
export function pickOffers<T extends { id: string }>(offers: readonly T[], id: string | undefined): T[] {
  if (id) {
    const one = offers.find((offer) => offer.id === id);
    if (one) return [one];
  }
  return [...offers];
}

/** Whether an Escape keypress closes a purchase overlay: not when a dialog
 *  inside it (sign-in, terms) is open or already handled it. */
export function closesOnEscape(event: Pick<KeyboardEvent, "key" | "defaultPrevented">, nestedOpen: boolean): boolean {
  return event.key === "Escape" && !event.defaultPrevented && !nestedOpen;
}
